import { makeStyles } from '@mui/styles';
import {
  GridCellProps,
  GridColumn as Column,
} from '@progress/kendo-react-grid';
import { LightPageHeader } from 'Components/Display';
import { PrimaryButton, SmartInputBlock } from 'Components/Form';
import {
  DateCell,
  FilterBar,
  FilteredMainGrid,
  GridButtonSection,
} from 'Components/Grid';
import { DebouncedTextFilterCell } from 'Components/Grid/FilterCells/DebouncedTextFilterCell';
import {
  FilterBarProvider,
  FormProvider,
  GridProvider,
  useFilterBarState,
  useFormState,
  useGlobalContext,
} from 'Context';
import { useColumnSelectorState } from 'Context/ColumnSelectorProvider';
import download from 'downloadjs';
import {
  defaultGridProps,
  FilterBarFilterType,
  FilterConfig,
  IFilterConfig,
  InputType,
  InvoicesChecksDefinition,
  InvoicesSearch,
  StaticFilterConfig,
} from 'Models';
import { Permission } from 'Models/Templates/Permission/Permission';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import React, { Fragment } from 'react';
import { toast } from 'react-toastify';
import { Col, Row } from 'reactstrap';
import {
  buildEndpoint,
  DEFAULT_DATETIME24H_FORMAT,
  useApiWorker,
  useGridSearch,
  useHasPermissions,
} from 'Utilities';

export enum InvoicesGridIds {
  DetailedSearch = 'detailedSearch',
}

export const Invoices: React.FC = () => {
  return (
    <GridProvider entityName="Invoices">
      <LightPageHeader tall noBorder className="page-header-shadow">
        <Row>
          <Col>
            <h1>Invoices</h1>
          </Col>
        </Row>
      </LightPageHeader>
      <br />
      <Row>
        <Col></Col>
      </Row>
      <FilterBarDetailedSearchGrid />
    </GridProvider>
  );
};

export const InvoicesChecksFilters: React.FC = () => {
  const useStyles = makeStyles({
    pageHeader: {
      alignItems: 'center',
      justifyContent: 'center',
      display: 'flex',
    },
    col: {
      width: '100%',
    },
    checksDiv: {
      margin: 5,
    },
    customChecks: {
      padding: 0,
      margin: 5,
    },
  });
  const classes = useStyles();
  const columns = [1, 2, 3, 4, 5, 6, 7];

  //Dynamic checks render from "InvoicesChecksDefinition" array
  //Each check has a defaultChecked property to check/uncheck initial value
  return (
    <Fragment>
      <LightPageHeader
        tall
        noBorder
        className={`page-header-shadow ${classes.pageHeader}`}
      >
        {columns.map((column) => {
          return (
            <div className={classes.checksDiv} key={`column_${column}`}>
              <Col className={classes.col}>
                {Object.values(InvoicesChecksDefinition)
                  .filter((x) => x.column === column)
                  .map((field, index) => {
                    return (
                      <SmartInputBlock
                        key={field.index}
                        name={`selectedIndexes[${field.index}]`}
                        label={field.label}
                        type={InputType.Checkbox}
                        className={classes.customChecks}
                      />
                    );
                  })}
              </Col>
            </div>
          );
        })}
      </LightPageHeader>
      <br />
      <Row>
        <Col></Col>
      </Row>
    </Fragment>
  );
};

const FilterBarDetailedSearchGrid: React.FC = () => {
  const invoicesSearch = new InvoicesSearch();

  return (
    <FormProvider formModel={invoicesSearch}>
      <InvoicesChecksFilters />
      <InvoicesGrid />
    </FormProvider>
  );
};

const InvoicesGrid: React.FC = () => {
  const [gridToggler] = useGridSearch();
  const { formValues } = useFormState();
  const API = useApiWorker();
  const exportToCsvEndpoint = buildEndpoint(
    ENDPOINTS.invoices.endpointString('ExportToCsv')
  );
  const columnSelectorState = useColumnSelectorState();

  const exportAction = async (params: any) => {
    try {
      const { data, headers } = await API.post(
        exportToCsvEndpoint,
        {
          ...formValues,
          ...params,
          timezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        {
          responseType: 'blob',
        }
      );

      const mimeType = headers['content-type'];
      var dateStamp = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        day: '2-digit',
        month: '2-digit',
      })
        .format(new Date())
        .toString()
        .replace(/[\/\\]/g, '');

      var filename = `${'invoice'}_${dateStamp}.${'csv'}`;
      download(data, filename, mimeType);
    } catch (err) {
      const error = err as any;
      toast.error(
        error ? error?.ErrorMessage : 'Error: Could not download file'
      );
    }
  };
  const filters: IFilterConfig[] = [
    new FilterConfig(
      'searchKey',
      FilterBarFilterType.SearchText,
      'Stradix Number, Claim Number'
    ),
    new FilterConfig(
      'referralDateFrom',
      FilterBarFilterType.DatePicker,
      'Referral Date From'
    ),
    new FilterConfig(
      'referralDateTo',
      FilterBarFilterType.DatePicker,
      'Referral Date To'
    ),
    new FilterConfig(
      'clients',
      FilterBarFilterType.AsyncDropdown,
      'Client',
      {
        readEndpoint: ENDPOINTS.clients.dropdownItems,
      },
      (value) => value.name,
      true
    ),
    new FilterConfig(
      'reportDateCompletedFrom',
      FilterBarFilterType.DatePicker,
      'Report Date Completed From'
    ),
    new FilterConfig(
      'reportDateCompletedTo',
      FilterBarFilterType.DatePicker,
      'Report Date Completed To'
    ),
    new FilterConfig(
      'closedDateFrom',
      FilterBarFilterType.DatePicker,
      'Case Closed Date From'
    ),
    new FilterConfig(
      'closedDateTo',
      FilterBarFilterType.DatePicker,
      'Case Closed Date To'
    ),
    new StaticFilterConfig('selectedIndexes'),
  ];

  const hasPermissions = useHasPermissions();

  const [global, setGlobal] = useGlobalContext();

  const shouldHideReviewedBy =
    global.currentUser?.isDoctor || global.currentUser?.isDoctorAdmin;

  if (!shouldHideReviewedBy) {
    filters.push(
      new FilterConfig(
        'reviewedBy',
        FilterBarFilterType.AsyncDropdown,
        'Reviewed By',
        {
          readEndpoint: buildEndpoint(
            ENDPOINTS.users.endpointString('userByRolDropdownItems'),
            'Professional'
          ),
        }
      )
    );
  }

  const hasManagedReviewerPermissions = hasPermissions([
    Permission.ManagedReviewerFilters,
  ]);
  const hasAdminReviewerFiltersPermissions = hasPermissions([
    Permission.AdminReviewerFilters,
  ]);

  if (hasManagedReviewerPermissions || hasAdminReviewerFiltersPermissions) {
    filters.push(
      new FilterConfig(
        'reviewers',
        FilterBarFilterType.AsyncDropdown,
        'Reviewer Attesting',
        {
          readEndpoint:
            ENDPOINTS.managedreviewers.endpointString('DropdownItems'),
        },
        (value) => value.name,
        true
      )
    );
  }

  //Column is visible if the boolean value is "True" in the current context
  //Report Title column is locked(fixed) by default
  return (
    <React.Fragment>
      <FilterBarProvider filterValues={[]} filterConfigurations={filters}>
        <FilterBar />

        <FilteredMainGrid
          isMainGrid
          forceExportAction={exportAction}
          refreshToggle={gridToggler}
          readEndpoint={ENDPOINTS.invoices.read}
          scrollable={'scrollable'}
          {...defaultGridProps}
        >
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.reportTitle.index
          ] && (
            <Column
              field="reportTitle"
              title="Report Title"
              width={'auto'}
              locked={true}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.claimantLastName.index
          ] && (
            <Column
              field="claimantLastName"
              title="Claimant Last Name"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.claimantFirstName.index
          ] && (
            <Column
              field="claimantFirstName"
              title="Claimant First Name"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.claimNumber.index
          ] && (
            <Column field="claimNumber" title="Claim Number" width={'auto'} />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.reportDueDate.index
          ] && (
            <Column
              field="reportDueDate"
              title="Due Date"
              cell={InputDateFormatCell}
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.reportDateCompleted.index
          ] && (
            <Column
              field="reportDateCompleted"
              title="Report Date Completed"
              cell={DateFormatCell}
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.closedDate.index
          ] && (
            <Column
              field="closedDate"
              title="Case Closed Date"
              cell={DateFormatCell}
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.benefitState.index
          ] && (
            <Column
              field="benefitState"
              title="State of Jurisdiction"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.reviewedBy.index
          ] && <Column field="reviewedBy" title="Reviewed By" width={'auto'} />}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.referralNumber.index
          ] && (
            <Column
              field="referralNumber"
              title="Referral Number"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.reportDeterminationDate.index
          ] && (
            <Column
              field="reportDeterminationDate"
              title="Determination Date"
              cell={DateFormatCell}
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[InvoicesChecksDefinition.rush.index] && (
            <Column
              field="rush"
              title="Rush"
              width={'auto'}
              cell={YesNoBoolParse}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.contact.index
          ] && <Column field="contact" title="Contact" width={'auto'} />}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.numberOfRequests.index
          ] && (
            <Column
              field="numberOfRequests"
              title="Number Of Requests"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.pageCount.index
          ] && <Column field="pageCount" title="Page Count" width={'auto'} />}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.reasonsForRequest.index
          ] && (
            <Column
              field="reasonsForRequest"
              title="Reasons For Request"
              width="250"
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.outcomeOfReview.index
          ] && (
            <Column
              field="outcomeOfReview"
              title="Outcome Of Review"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.requestedReviewerSpecialty1.index
          ] && (
            <Column
              field="requestedReviewerSpecialties[0]"
              title="Requested Reviewer Specialty 1"
              width={'auto'}
              cell={(props) => (
                <RequestedReviewerSpecialtiesCell number={0} {...props} />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.requestedReviewerSpecialty2.index
          ] && (
            <Column
              field="requestedReviewerSpecialties[1]"
              title="Requested Reviewer Specialty 2"
              width={'auto'}
              cell={(props) => (
                <RequestedReviewerSpecialtiesCell number={1} {...props} />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.requestedReviewerSpecialty3.index
          ] && (
            <Column
              field="requestedReviewerSpecialties[2]"
              title="Requested Reviewer Specialty 3"
              width={'auto'}
              cell={(props) => (
                <RequestedReviewerSpecialtiesCell number={2} {...props} />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.requestedReviewerSpecialty4.index
          ] && (
            <Column
              field="requestedReviewerSpecialties[3]"
              title="Requested Reviewer Specialty 4"
              width={'auto'}
              cell={(props) => (
                <RequestedReviewerSpecialtiesCell number={3} {...props} />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.requestedReviewerSpecialty5.index
          ] && (
            <Column
              field="requestedReviewerSpecialties[4]"
              title="Requested Reviewer Specialty 5"
              width={'auto'}
              cell={(props) => (
                <RequestedReviewerSpecialtiesCell number={4} {...props} />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.sameDay.index
          ] && <Column field="sameDay" title="Same Day" width={'auto'} />}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.claimantClient.index
          ] && <Column field="claimantClient" title="Client" width={'auto'} />}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.employerName.index
          ] && (
            <Column field="employerName" title="Employer Name" width={'auto'} />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.insuranceCarrier.index
          ] && (
            <Column
              field="insuranceCarrier"
              title="Insurance Carrier"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.referralSource.index
          ] && (
            <Column
              field="referralSource"
              title="Referral Source"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.externalAdjuster.index
          ] && (
            <Column
              field="externalAdjuster"
              title="External Adjuster"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.physicianSpecialty.index
          ] && (
            <Column
              field="physicianSpecialty"
              title="Physician Specialty"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.referralDate.index
          ] && (
            <Column
              field="referralDate"
              title="Referral Date"
              cell={DateFormatCell}
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.callAttempts.index
          ] && (
            <Column field="callAttempts" title="Call Attempts" width={'auto'} />
          )}
          {/** add stradixNumber column */}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.stradixNumber.index
          ] && (
            <Column
              field="stradixNumber"
              title="Stradix Number"
              width="150px"
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.lineOfBusiness.index
          ] && (
            <Column
              field="lineOfBusiness"
              title="Line Of Business"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.service.index
          ] && <Column field="service" title="Service" width={'auto'} />}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.subService.index
          ] && <Column field="subService" title="Sub Service" width={'auto'} />}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.claimantGender.index
          ] && (
            <Column
              field="claimantGender"
              title="Claimant Gender"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.claimantStreetAddress.index
          ] && (
            <Column
              field="claimantStreetAddress"
              title="Claimant Street Address"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.claimantCity.index
          ] && (
            <Column field="claimantCity" title="Claimant City" width={'auto'} />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.claimantState.index
          ] && (
            <Column
              field="claimantState"
              title="Claimant State"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.claimantZipCode.index
          ] && (
            <Column
              field="claimantZipCode"
              title="Claimant Zip Code"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.claimantEmail.index
          ] && (
            <Column
              field="claimantEmail"
              title="Claimant Email"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.dateOfInjury.index
          ] && (
            <Column
              field="dateOfInjury"
              title="Date of Injury/Disability"
              cell={InputDateFormatCell}
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.dateOfRequest.index
          ] && (
            <Column
              field="dateOfRequest"
              title="Date Of Request"
              cell={InputDateFormatCell}
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.dateOfExam.index
          ] && (
            <Column
              field="dateOfExam"
              title="Date Of Exam"
              cell={InputDateFormatCell}
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.claimantDateOfBirth.index
          ] && (
            <Column
              field="claimantDateOfBirth"
              title="Claimant Date Of Birth"
              cell={InputDateFormatCell}
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.tatPriority.index
          ] && (
            <Column field="tatPriority" title="TAT Priority" width={'auto'} />
          )}
          {formValues.selectedIndexes[InvoicesChecksDefinition.tat.index] && (
            <Column field="tat" title="TAT" width={'auto'} />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.jurisditionalMatchRequired.index
          ] && (
            <Column
              field="jurisditionalMatchRequired"
              title="Jurisditional Match Required"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.apContactPreference.index
          ] && (
            <Column
              field="apContactPreference"
              title="AP Contact Preference"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.apName1.index
          ] && (
            <Column
              field="attendingProviders[0]"
              title="AP Name 1"
              width={'auto'}
              cell={(props) => (
                <AttendingProviderCell
                  property="displayName"
                  number={0}
                  {...props}
                />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.apName2.index
          ] && (
            <Column
              field="attendingProviders[1]"
              title="AP Name 2"
              width={'auto'}
              cell={(props) => (
                <AttendingProviderCell
                  property="displayName"
                  number={1}
                  {...props}
                />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.apName3.index
          ] && (
            <Column
              field="attendingProviders[2]"
              title="AP Name 3"
              width={'auto'}
              cell={(props) => (
                <AttendingProviderCell
                  property="displayName"
                  number={2}
                  {...props}
                />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.apName4.index
          ] && (
            <Column
              field="attendingProviders[3]"
              title="AP Name 4"
              width={'auto'}
              cell={(props) => (
                <AttendingProviderCell
                  property="displayName"
                  number={3}
                  {...props}
                />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.apName5.index
          ] && (
            <Column
              field="attendingProviders[4]"
              title="AP Name 5"
              width={'auto'}
              cell={(props) => (
                <AttendingProviderCell
                  property="displayName"
                  number={4}
                  {...props}
                />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.apSpecialty1.index
          ] && (
            <Column
              field="attendingProviders[0]"
              title="AP Specialty 1"
              width={'auto'}
              cell={(props) => (
                <AttendingProviderCell
                  property="specialty"
                  number={0}
                  {...props}
                />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.apSpecialty2.index
          ] && (
            <Column
              field="attendingProviders[1]"
              title="AP Specialty 2"
              width={'auto'}
              cell={(props) => (
                <AttendingProviderCell
                  property="specialty"
                  number={1}
                  {...props}
                />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.apSpecialty3.index
          ] && (
            <Column
              field="attendingProviders[2]"
              title="AP Specialty 3"
              width={'auto'}
              cell={(props) => (
                <AttendingProviderCell
                  property="specialty"
                  number={2}
                  {...props}
                />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.apSpecialty4.index
          ] && (
            <Column
              field="attendingProviders[3]"
              title="AP Specialty 4"
              width={'auto'}
              cell={(props) => (
                <AttendingProviderCell
                  property="specialty"
                  number={3}
                  {...props}
                />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.apSpecialty5.index
          ] && (
            <Column
              field="attendingProviders[4]"
              title="AP Specialty 5"
              width={'auto'}
              cell={(props) => (
                <AttendingProviderCell
                  property="specialty"
                  number={4}
                  {...props}
                />
              )}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.attorneyName.index
          ] && (
            <Column field="attorneyName" title="Attorney Name" width={'auto'} />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.numberOfReviews.index
          ] && (
            <Column
              field="numberOfReviews"
              title="Number Of Reviews"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.referralStatus.index
          ] && (
            <Column
              field="referralStatus"
              title="Referral Status"
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.dueDateAndTime.index
          ] && (
            <Column
              field="dueDateAndTime"
              title="Due Date & Time"
              cell={InputDateTimeFormatCell}
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.dateAndTimeRequestReceived.index
          ] && (
            <Column
              field="dateAndTimeRequestReceived"
              title="Date and Time Request Received"
              cell={dateTimeFormatCell}
              width={'auto'}
            />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.proxiedByUser.index
          ] && (
            <Column field="proxiedByUser" title="Proxied By" width={'auto'} />
          )}
          {formValues.selectedIndexes[
            InvoicesChecksDefinition.ownerName.index
          ] && (
            <Column
              field="ownerName"
              title="Owner"
              width={'auto'}
              filterCell={DebouncedTextFilterCell}
            />
          )}
          <Column field="id" title=" " className="hidden" />
        </FilteredMainGrid>
      </FilterBarProvider>
    </React.Fragment>
  );
};

const dateTimeFormatCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return <DateCell {...props} dateFormat={DEFAULT_DATETIME24H_FORMAT} />;
};
const InputDateTimeFormatCell: React.FC<GridCellProps> = (
  props: GridCellProps
) => {
  return <DateCell {...props} dateFormat={DEFAULT_DATETIME24H_FORMAT} />;
};
interface AttendingProviderCellProps extends GridCellProps {
  number?: number;
  property: 'displayName' | 'specialty';
}
const AttendingProviderCell: React.FC<AttendingProviderCellProps> = ({
  number = 0,
  property = 'displayName',
  ...props
}) => {
  const attendingProvider = props.dataItem.attendingProviders
    ? props.dataItem.attendingProviders[number]
    : null;

  if (property === 'specialty') {
    return <td>{attendingProvider?.apSpecialty?.displayName}</td>;
  } else {
    return <td>{attendingProvider?.apName}</td>;
  }
};
interface RequestedReviewerSpecialtiesCellProps extends GridCellProps {
  number?: number;
}
const RequestedReviewerSpecialtiesCell: React.FC<
  RequestedReviewerSpecialtiesCellProps
> = ({ number = 0, ...props }) => {
  const specialty = props.dataItem.requestedReviewerSpecialties
    ? props.dataItem.requestedReviewerSpecialties[number]
    : null;

  if (!specialty) {
    return <td>{''}</td>;
  }
  return <td>{specialty.displayName}</td>;
};

const DateFormatCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return <DateCell {...props} dateFormat={'MM/DD/YYYY'} />;
};

const InputDateFormatCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return <DateCell {...props} dateFormat={'MM/DD/YYYY'} />;
};

const YesNoBoolParse: React.FC<GridCellProps> = (props: GridCellProps) => {
  const value = props.dataItem[props.field as string] as string;
  const boolValue = value.toLowerCase() === 'true';

  let result = boolValue ? 'Yes' : 'No';

  return <td>{result}</td>;
};

// const FormatCell: React.FC<GridCellProps> = (props: GridCellProps) => {
//   const value = props.dataItem[props.field as string] as string;

//   if (!value) {
//     return <td>{''}</td>;
//   }

//   const formattedValue = value
//     .toLowerCase()
//     .split(' ')
//     .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
//     .join(' ');

//   return <td>{formattedValue}</td>;
// };

const getSelectedColumnsQuery = (columnSelectorState: any) => {
  const selectedColumns = columnSelectorState.columns
    .filter((x: any) => x.checked)
    .map((x: any) => x.field);
  return '?' + selectedColumns.map((x: any) => `columns=${x}`).join('&');
};
